import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBankFeedStatus,
  startBankLink,
  removeBankConnection,
  syncBankFeed,
  getBankFeedInbox,
  approveBankFeedItems,
  dismissBankFeedItems,
  listFinanceAccounts,
  ApiError,
  type BankConnection,
  type BankFeedInboxItem,
  type TxDirection,
} from "@/lib/api";
import { fmtMoney } from "@/lib/money";

export function BankFeedTab() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["bank-feed"], queryFn: getBankFeedStatus });
  const inbox = useQuery({ queryKey: ["bank-feed-inbox"], queryFn: getBankFeedInbox });
  const { data: accounts } = useQuery({
    queryKey: ["fin-accounts"],
    queryFn: listFinanceAccounts,
  });
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["bank-feed"] });
    qc.invalidateQueries({ queryKey: ["bank-feed-inbox"] });
  };

  const connect = useMutation({
    mutationFn: startBankLink,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not start bank link"),
  });

  const sync = useMutation({
    mutationFn: syncBankFeed,
    onSuccess: (r) => {
      setResult(`Checked the bank: ${r.added} new transaction${r.added === 1 ? "" : "s"}.`);
      setTimeout(() => setResult(null), 4000);
      refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Sync failed"),
  });

  // Surface the redirect outcome (…/finance?feed=connected|error) once.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("feed") === "connected") setResult("Bank connected — first sync has run.");
    if (q.get("feed") === "error") setError("Bank connection failed — try again.");
    if (q.has("feed")) window.history.replaceState({}, "", "/finance");
  }, []);

  return (
    <div>
      {error && (
        <p className="mb-4 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">
          <i className="fa-solid fa-triangle-exclamation mr-1.5" />
          {error}
        </p>
      )}
      {result && (
        <p className="mb-4 rounded-token-md bg-accent-muted px-3 py-2 text-sm text-accent">
          <i className="fa-solid fa-circle-check mr-1.5" />
          {result}
        </p>
      )}

      {/* Connections */}
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Connected accounts
            {status.data && (
              <span className="ml-2 normal-case tracking-normal">
                via {status.data.provider}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            {status.data && status.data.connections.length > 0 && (
              <button
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                className="rounded-token-md border border-border px-3 py-2 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading disabled:opacity-50"
              >
                <i
                  className={`fa-solid ${sync.isPending ? "fa-circle-notch fa-spin" : "fa-rotate"} mr-1.5 text-xs`}
                />
                Sync now
              </button>
            )}
            <button
              onClick={() => connect.mutate()}
              disabled={connect.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ color: "var(--color-on-accent)" }}
            >
              <i
                className={`fa-solid ${connect.isPending ? "fa-circle-notch fa-spin" : "fa-building-columns"} mr-1.5 text-xs`}
              />
              Connect bank
            </button>
          </div>
        </div>

        {status.isLoading ? (
          <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-10 text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin" />
          </div>
        ) : !status.data || status.data.connections.length === 0 ? (
          <div className="rounded-token-lg border border-dashed border-border bg-bg-card/40 px-6 py-10 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-token-lg bg-accent-muted text-xl text-accent">
              <i className="fa-solid fa-building-columns" />
            </div>
            <h3 className="mt-3 font-display text-base font-semibold text-text-heading">
              No bank connected yet
            </h3>
            <p className="mx-auto mt-1.5 max-w-md text-sm text-text-secondary">
              Connect the HSBC business account once and new transactions appear here
              automatically every hour — no more CSV downloads. You approve them into
              the ledger; nothing posts without review.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {status.data.connections.map((c) => (
              <ConnectionCard key={c.id} conn={c} onChanged={refresh} />
            ))}
          </div>
        )}
      </div>

      {/* Inbox */}
      <InboxSection
        items={inbox.data ?? []}
        loading={inbox.isLoading}
        accounts={accounts ?? []}
        onChanged={refresh}
        onError={setError}
        onResult={(m) => {
          setResult(m);
          setTimeout(() => setResult(null), 4000);
        }}
      />
    </div>
  );
}

function ConnectionCard({
  conn,
  onChanged,
}: {
  conn: BankConnection;
  onChanged: () => void;
}) {
  const remove = useMutation({
    mutationFn: () => removeBankConnection(conn.id),
    onSuccess: onChanged,
  });

  const expiresInDays = conn.consentExpiresAt
    ? Math.floor((new Date(conn.consentExpiresAt).getTime() - Date.now()) / 86_400_000)
    : null;
  const expiringSoon = expiresInDays != null && expiresInDays <= 14;

  const statusMeta =
    conn.status === "linked"
      ? { label: "Connected", color: "var(--color-success)" }
      : conn.status === "expired"
        ? { label: "Consent expired", color: "var(--color-warning)" }
        : { label: "Error", color: "var(--color-danger)" };

  return (
    <div className="rounded-token-lg border border-border bg-bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text-heading">
            <i className="fa-solid fa-building-columns mr-2 text-xs text-text-muted" />
            {conn.accountName ?? "Bank account"}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-xs" style={{ color: statusMeta.color }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: statusMeta.color }} />
            {statusMeta.label}
          </p>
        </div>
        <button
          onClick={() => {
            if (confirm("Disconnect this bank account? Pending inbox items are removed."))
              remove.mutate();
          }}
          title="Disconnect"
          className="grid h-7 w-7 shrink-0 place-items-center rounded text-text-muted hover:bg-danger/10 hover:text-danger"
        >
          <i className="fa-solid fa-link-slash text-xs" />
        </button>
      </div>

      <div className="mt-3 space-y-1 border-t border-border-light pt-2 text-[11px] text-text-muted">
        {conn.lastSyncedAt && (
          <p>Last synced {new Date(conn.lastSyncedAt).toLocaleString()}</p>
        )}
        {expiresInDays != null && (
          <p className={expiringSoon ? "font-medium text-warning" : ""}>
            {expiresInDays >= 0
              ? `Consent renewal due in ${expiresInDays} day${expiresInDays === 1 ? "" : "s"}`
              : "Consent lapsed — reconnect to resume the feed"}
          </p>
        )}
        {conn.lastSyncError && (
          <p className="text-danger">Last sync error: {conn.lastSyncError}</p>
        )}
      </div>
    </div>
  );
}

function InboxSection({
  items,
  loading,
  accounts,
  onChanged,
  onError,
  onResult,
}: {
  items: BankFeedInboxItem[];
  loading: boolean;
  accounts: { id: string; name: string; type: string; archived: boolean }[];
  onChanged: () => void;
  onError: (m: string) => void;
  onResult: (m: string) => void;
}) {
  const [include, setInclude] = useState<Record<string, boolean>>({});
  const [category, setCategory] = useState<Record<string, string>>({});

  const paidVia = accounts.filter((a) => !a.archived && a.type === "asset");
  const bank = paidVia.find((a) => a.name === "Bank");
  const [paidAccountId, setPaidAccountId] = useState("");
  const effectivePaid = paidAccountId || bank?.id || paidVia[0]?.id || "";

  const categoriesFor = (direction: TxDirection) =>
    accounts.filter(
      (a) =>
        !a.archived &&
        (direction === "out"
          ? a.type === "expense"
          : a.type === "income" || a.type === "equity"),
    );

  const isIncluded = (it: BankFeedInboxItem) => include[it.id] ?? true;
  const categoryOf = (it: BankFeedInboxItem) =>
    category[it.id] ?? it.suggestedAccountId ?? "";

  const selected = items.filter(isIncluded);
  const missing = selected.filter((it) => !categoryOf(it)).length;

  const approve = useMutation({
    mutationFn: () =>
      approveBankFeedItems(
        selected.map((it) => ({ id: it.id, accountId: categoryOf(it) })),
        effectivePaid,
      ),
    onSuccess: (r) => {
      onResult(
        `Posted ${r.created} transaction${r.created === 1 ? "" : "s"} to the ledger` +
          (r.skippedDuplicates ? ` (${r.skippedDuplicates} duplicates skipped)` : "") +
          ".",
      );
      setInclude({});
      setCategory({});
      onChanged();
    },
    onError: (e) => onError(e instanceof ApiError ? e.message : "Approve failed"),
  });

  const dismiss = useMutation({
    mutationFn: () => dismissBankFeedItems(selected.map((it) => it.id)),
    onSuccess: onChanged,
  });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Inbox
          {items.length > 0 && (
            <span className="ml-1.5 rounded-full bg-bg-tertiary px-1.5 text-[10px] text-text-muted">
              {items.length}
            </span>
          )}
        </h2>
        {items.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-text-muted">
              Paid via
              <select
                className="rounded-token-md border border-border bg-bg px-2 py-1 text-xs text-text outline-none focus:border-accent"
                value={effectivePaid}
                onChange={(e) => setPaidAccountId(e.target.value)}
              >
                {paidVia.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={() => dismiss.mutate()}
              disabled={selected.length === 0 || dismiss.isPending}
              className="rounded-token-md border border-border px-3 py-1.5 text-xs text-text-secondary transition-colors hover:text-text-heading disabled:opacity-50"
            >
              Dismiss
            </button>
            <button
              onClick={() => approve.mutate()}
              disabled={selected.length === 0 || missing > 0 || approve.isPending}
              title={missing > 0 ? `${missing} selected row(s) missing a category` : ""}
              className="rounded-token-md bg-accent text-on-accent px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ color: "var(--color-on-accent)" }}
            >
              {approve.isPending ? "Posting…" : `Approve ${selected.length} to ledger`}
            </button>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
        {loading ? (
          <div className="grid place-items-center py-10 text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-text-muted">
            Nothing waiting for review — new bank transactions land here automatically.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-[0.6rem] uppercase tracking-wider text-text-muted">
                <th className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={items.every(isIncluded)}
                    onChange={(e) =>
                      setInclude(
                        Object.fromEntries(items.map((it) => [it.id, e.target.checked])),
                      )
                    }
                  />
                </th>
                <th className="w-24 px-3 py-2 font-semibold">Date</th>
                <th className="px-3 py-2 font-semibold">Description</th>
                <th className="w-24 px-3 py-2 text-right font-semibold">Amount</th>
                <th className="w-44 px-3 py-2 font-semibold">Category</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border-light">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={isIncluded(it)}
                      onChange={(e) =>
                        setInclude((m) => ({ ...m, [it.id]: e.target.checked }))
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-text-muted">{it.date}</td>
                  <td className="max-w-0 px-3 py-2">
                    <span className="block truncate text-text" title={it.description}>
                      {it.description}
                    </span>
                    {it.accountName && (
                      <span className="text-[0.6rem] text-text-muted">{it.accountName}</span>
                    )}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2 text-right font-medium"
                    style={{
                      color:
                        it.direction === "in"
                          ? "var(--color-success)"
                          : "var(--color-danger)",
                    }}
                  >
                    {it.direction === "in" ? "+" : "−"}
                    {fmtMoney(it.amount)}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className={`w-full rounded border bg-bg px-1.5 py-1 text-xs outline-none focus:border-accent ${
                        isIncluded(it) && !categoryOf(it) ? "border-warning" : "border-border"
                      }`}
                      value={categoryOf(it)}
                      onChange={(e) =>
                        setCategory((m) => ({ ...m, [it.id]: e.target.value }))
                      }
                    >
                      <option value="">Choose…</option>
                      {categoriesFor(it.direction).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-text-muted">
        <i className="fa-regular fa-lightbulb mr-1" />
        The feed checks the bank hourly. Duplicates (including anything already
        CSV-imported) are filtered out before they reach this inbox, and the label
        used here teaches future category suggestions.
      </p>
    </div>
  );
}
